import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from './ui/command';
import {
  Home,
  AlertCircle,
  FileText,
  Users,
  Settings,
  Plus,
  Search,
  HelpCircle,
  LayoutDashboard,
  Ticket,
  Database,
  Plug,
  BookOpen,
  BarChart3,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Listen for keyboard shortcuts
  useEffect(() => {
    const down = (e) => {
      // Ctrl+K or Cmd+K to open command palette
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }

      // Escape to close
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [navigate, open]);

  const runCommand = useCallback((command) => {
    setOpen(false);
    command();
  }, []);

  // Navigation items based on user role
  const getNavigationItems = () => {
    const items = [
      { 
        name: 'Dashboard', 
        icon: LayoutDashboard, 
        path: '/dashboard',
        keywords: ['home', 'main', 'overview']
      },
      { 
        name: 'Incidents', 
        icon: AlertCircle, 
        path: '/incidents',
        keywords: ['tickets', 'issues', 'problems']
      },
    ];

    // Add items based on role
    if (user?.role === 'itil_staff' || user?.role === 'admin' || user?.role === 'super_admin') {
      items.push(
        { name: 'Request Fulfillment', icon: FileText, path: '/service-requests-queue', keywords: ['requests', 'sr', 'fulfill', 'service requests'] },
        { name: 'Queue', icon: Ticket, path: '/queue', keywords: ['my queue', 'assigned'] },
        { name: 'Knowledge Management', icon: BookOpen, path: '/knowledge-management', keywords: ['kb', 'articles', 'help', 'knowledge'] },
        { name: 'Reports', icon: BarChart3, path: '/reports', keywords: ['analytics', 'stats'] },
      );
    }

    if (user?.role === 'admin' || user?.role === 'super_admin') {
      items.push(
        { name: 'Users', icon: Users, path: '/users', keywords: ['people', 'accounts'] },
        { name: 'Assets', icon: Database, path: '/assets', keywords: ['inventory', 'equipment'] },
        { name: 'Integrations', icon: Plug, path: '/integrations', keywords: ['connect', 'email', 'google'] },
        { name: 'SLA Configuration', icon: Settings, path: '/sla-config', keywords: ['sla', 'settings'] },
      );
    }

    return items;
  };

  const quickActions = [
    { 
      name: 'Create New Incident', 
      icon: Plus, 
      action: () => navigate('/incidents/new'),
      shortcut: 'N',
      keywords: ['new', 'create', 'ticket']
    },
    { 
      name: 'Search Incidents', 
      icon: Search, 
      action: () => navigate('/incidents'),
      keywords: ['find', 'lookup']
    },
    { 
      name: 'Help Center', 
      icon: HelpCircle, 
      action: () => navigate('/help'),
      keywords: ['support', 'faq']
    },
  ];

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search..." data-testid="command-palette-input" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        
        <CommandGroup heading="Quick Actions">
          {quickActions.map((item) => (
            <CommandItem
              key={item.name}
              onSelect={() => runCommand(item.action)}
              data-testid={`cmd-action-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="mr-2 h-4 w-4" />
              <span>{item.name}</span>
              {item.shortcut && (
                <CommandShortcut>{item.shortcut}</CommandShortcut>
              )}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Navigation">
          {getNavigationItems().map((item) => (
            <CommandItem
              key={item.name}
              onSelect={() => runCommand(() => navigate(item.path))}
              data-testid={`cmd-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="mr-2 h-4 w-4" />
              <span>{item.name}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Help">
          <CommandItem
            onSelect={() => runCommand(() => setOpen(false))}
            data-testid="cmd-shortcuts-help"
          >
            <span className="text-muted-foreground text-xs">
              Press <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Ctrl+K</kbd> to open this menu
            </span>
          </CommandItem>
          <CommandItem
            onSelect={() => runCommand(() => navigate('/incidents/new'))}
            data-testid="cmd-shortcuts-new"
          >
            <span className="text-muted-foreground text-xs">
              Press <kbd className="px-1 py-0.5 bg-muted rounded text-xs">N</kbd> to create new incident
            </span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

// Export a keyboard shortcut indicator component
export function KeyboardShortcutHint({ shortcut, className = '' }) {
  return (
    <kbd className={`px-1.5 py-0.5 text-xs font-mono bg-muted border rounded ${className}`}>
      {shortcut}
    </kbd>
  );
}

export default CommandPalette;
